import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { ORIGIN_RESOURCE_PATHS } from "contracts/api/yeet/origin-subjects";
import { BLOCK_TYPE } from "contracts/enums/metrics";
import { BRIEF_SOURCE_TYPE, BRIEF_STATUS } from "../../enums/brief";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import { currentPageEvidenceForGroup } from "../scan/issuePageEvidence";
import { currentIssuePageGroups } from "../scan/issuePageResolution";
import {
  clientIssueTitle,
  issueBlocksForSite,
  issueDimensionLabel,
  siteSpecificRecommendationsForIssue,
} from "../scan/issueText";
import { isOpen } from "../website/issueModel";
import { genericIssueBriefPresentation } from "./briefGenericIssue";
import type {
  BriefEvidence,
  BriefPageSource,
  DeliveryBrief,
  IssueBriefDraft,
} from "./briefTypes";

const INELIGIBLE_ISSUE_COPY =
  "This issue does not yet contain enough delivery detail to create a Brief.";

export function buildIssueBrief(
  data: OverviewResponse,
  issueId: string,
): DeliveryBrief | null {
  const issue = data.issues.find((candidate) => candidate.id === issueId);
  if (!issue || !issueBriefEligibility(data, issue).eligible) return null;
  return baseIssueBrief(data, issue, genericIssueBrief(data, issue));
}

export function issueBriefEligibility(
  data: OverviewResponse,
  issue: Issue,
): { eligible: boolean; reason: string | null } {
  const hasScope =
    issue.originResourceKind !== null ||
    currentIssuePageGroups(data, issue).length > 0;
  const hasRecommendation =
    siteSpecificRecommendationsForIssue(data, issue).length > 0;
  const hasContext =
    issue.primarySubcriterionKey !== null ||
    issue.originResourceKind !== null ||
    issueEvidence(data, issue).length > 0;
  const eligible =
    isOpen(issue) &&
    issue.title.trim().length > 0 &&
    hasScope &&
    hasRecommendation &&
    hasContext;
  return {
    eligible,
    reason: eligible ? null : INELIGIBLE_ISSUE_COPY,
  };
}

function genericIssueBrief(
  data: OverviewResponse,
  issue: Issue,
): IssueBriefDraft {
  const sourceAction =
    siteSpecificRecommendationsForIssue(data, issue)[0] ?? "";
  const pages = issuePages(data, issue);
  const scope = issueScope(issue, pages);
  const presentation = genericIssueBriefPresentation(data, issue, sourceAction);
  return {
    title: presentation.title,
    objective: presentation.objective,
    currentProblem: presentation.currentProblem,
    requiredChange: sourceAction,
    scopeNotes: scope.notes,
    outOfScopeNotes: "Unrelated page findings are outside this brief.",
    requirements: sourceAction
      ? [
          {
            id: "source-action",
            title: "Proposed change",
            items: [sourceAction],
          },
        ]
      : [],
    acceptanceCriteria: presentation.acceptanceCriteria,
    questions: ["Which team owns this change?"],
  };
}

function issueScope(issue: Issue, pages: BriefPageSource[]): { notes: string } {
  if (issue.originResourceKind) {
    const path = ORIGIN_RESOURCE_PATHS[issue.originResourceKind];
    const observations = pages.length
      ? `, supported by ${pages.length} affected page ${pages.length === 1 ? "observation" : "observations"}`
      : "";
    return {
      notes: `The ${path} origin resource${observations}.`,
    };
  }
  return {
    notes: `${pages.length} affected ${pages.length === 1 ? "page" : "pages"} recorded by the current issue.`,
  };
}

function baseIssueBrief(
  data: OverviewResponse,
  issue: Issue,
  draft: IssueBriefDraft,
): DeliveryBrief {
  const pages = issuePages(data, issue);
  return {
    id: `issue-${issue.id}`,
    sourceType: BRIEF_SOURCE_TYPE.ISSUE,
    ...draft,
    openQuestions: draft.questions.map((text, index) => ({
      id: `issue-question-${index}`,
      text,
      response: "",
      confirmationSource: "",
    })),
    owner: issue.ownerName ?? "Not assigned",
    status: BRIEF_STATUS.NEEDS_INPUT,
    pages,
    source: {
      type: BRIEF_SOURCE_TYPE.ISSUE,
      id: issue.id,
      label: "Scan issue",
      title: clientIssueTitle(issue),
      route: `/w/${data.site.websiteId}/scan/issues/${issue.id}`,
      dimension: issueDimensionLabel(issue),
      evidence: issueEvidence(data, issue),
      guidance: [],
      routes: [
        {
          label: "Open source issue",
          href: `/w/${data.site.websiteId}/scan/issues/${issue.id}`,
        },
        ...pages.flatMap((page) =>
          page.detailRoute
            ? [{ label: `Open ${page.title}`, href: page.detailRoute }]
            : [],
        ),
      ],
      technical: [
        { label: "Issue ID", value: issue.id },
        { label: "Fingerprint", value: issue.fingerprint },
        { label: "First seen job", value: issue.firstSeenJobId },
        { label: "Last seen job", value: issue.lastSeenJobId },
      ],
      coverageNote:
        pages.length === 0 && issue.originResourceKind
          ? `This brief concerns the ${ORIGIN_RESOURCE_PATHS[issue.originResourceKind]} origin resource rather than a scored page.`
          : pages.some((page) => !page.assessed)
            ? "This brief includes pages that were not assessed."
            : `${pages.length} affected ${pages.length === 1 ? "page has" : "pages have"} assessment data.`,
    },
  };
}

function issuePages(data: OverviewResponse, issue: Issue): BriefPageSource[] {
  return currentIssuePageGroups(data, issue).map(({ page }) => ({
    id: page.id,
    title: pageDisplayLabel(page, data.site),
    path: rawPagePath(page),
    detailRoute: `/w/${data.site.websiteId}/scan/pages/${page.id}`,
    contentFitnessScore: page.contentFitness?.score ?? null,
    assessed: page.contentFitness !== null,
  }));
}

function pageEvidence(data: OverviewResponse, issue: Issue): BriefEvidence[] {
  return currentIssuePageGroups(data, issue).flatMap((group, pageIndex) => {
    const evidence = currentPageEvidenceForGroup(data, group);
    return [
      ...evidence.readable.map((item, evidenceIndex) => ({
        id: `page-evidence-${pageIndex}-${evidenceIndex}`,
        label: group.page.path,
        detail: item.value,
        kind: "readable" as const,
      })),
      ...evidence.technical.map((item, evidenceIndex) => ({
        id: `page-locator-${pageIndex}-${evidenceIndex}`,
        label: `${group.page.path} locator`,
        detail: item.value,
        kind: "technical" as const,
      })),
    ];
  });
}

function issueEvidence(data: OverviewResponse, issue: Issue): BriefEvidence[] {
  const blocks = issueBlocksForSite(data, issue).flatMap((block, index) =>
    block.type === BLOCK_TYPE.EVIDENCE
      ? [
          {
            id: `issue-evidence-${index}`,
            label: "Issue evidence",
            detail: block.body,
            kind: "readable" as const,
          },
        ]
      : [],
  );
  return [...blocks, ...pageEvidence(data, issue)];
}
